/** Role assignment and audio selection — the two policy decisions in a plan. */
import { MEDIA_LIMITS, QUALITY_PROFILES, type QualityProfile } from '@cyphex/types';
import type { MediaParticipant } from './contract';
import type { TileRole } from './densityPolicy';
import type { MeetingLayout } from './tilePlan';

export interface RoleInput {
  readonly layout: MeetingLayout;
  readonly pinnedId: string | null;
  readonly activeSpeakerIds: readonly string[];
  readonly visible: readonly MediaParticipant[];
}

export function roleForTile(index: number, input: RoleInput): TileRole {
  if (input.layout === 'presentation') return 'thumbnail';
  if (input.layout === 'speaker') return index === 0 ? 'spotlight' : 'thumbnail';

  const tile = input.visible[index];
  if (tile?.spotlighted || tile?.id === input.pinnedId) return 'spotlight';
  if (input.visible.length <= 4) return 'grid';
  // Density mode: only the top-3 recent speakers keep the grid ceiling.
  return input.activeSpeakerIds.slice(0, 3).includes(tile?.id ?? '') ? 'speaker' : 'secondary';
}

export interface AudioInput {
  readonly profile: QualityProfile;
  readonly localId: string;
  readonly activeSpeakerIds: readonly string[];
  readonly participants: readonly MediaParticipant[];
}

/** Top-N active speakers only. This is what turns O(N²) audio into O(3N). */
export function audioSubscriptions(input: AudioInput): readonly string[] {
  const limit = Math.min(
    QUALITY_PROFILES[input.profile].audioSubscriptions,
    MEDIA_LIMITS.MAX_AUDIO_SUBSCRIPTIONS,
  );
  const picked: string[] = [];

  for (const id of input.activeSpeakerIds) {
    if (id !== input.localId && !picked.includes(id) && picked.length < limit) picked.push(id);
  }
  // Keep the set warm so an unmute is not half a second of silence.
  for (const participant of input.participants) {
    if (picked.length >= limit) break;
    if (!participant.isLocal && participant.micOn && !picked.includes(participant.id)) {
      picked.push(participant.id);
    }
  }

  return picked;
}
